import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import JobCard from '@/components/JobCard.jsx';
import apiServerClient from '@/lib/apiServerClient.js';

const FeaturedJobsCarousel = () => {
  const [jobs, setJobs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedJobs = async () => {
      try {
        const response = await apiServerClient.fetch('/homepage-jobs');
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Could not load featured jobs');
        setJobs(data.jobs || []);
      } catch (error) {
        console.error('Error fetching featured jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedJobs();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, jobs.length - 2));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + Math.max(1, jobs.length - 2)) % Math.max(1, jobs.length - 2));
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-muted rounded-2xl h-[440px] animate-pulse" />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 bg-muted/30 rounded-3xl border border-border/50 text-center">
        <div className="bg-background p-6 rounded-full mb-6 shadow-sm border border-border/50">
          <Briefcase className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="mb-4">No featured roles</h3>
        <p className="text-section-desc max-w-xl">
          No featured jobs available at this time. Please check back later or use the search bar to find premium opportunities.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {jobs.slice(currentIndex, currentIndex + 3).map((job) => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>

      {jobs.length > 3 && (
        <div className="flex justify-center mt-12 space-x-6">
          <Button
            onClick={prevSlide}
            variant="outline"
            size="icon"
            className="rounded-full h-14 w-14 transition-all duration-200 hover:bg-primary hover:text-primary-foreground hover:shadow-lg active:scale-[0.98]"
            aria-label="Previous jobs"
          >
            <ChevronLeft size={28} />
          </Button>
          <Button
            onClick={nextSlide}
            variant="outline"
            size="icon"
            className="rounded-full h-14 w-14 transition-all duration-200 hover:bg-primary hover:text-primary-foreground hover:shadow-lg active:scale-[0.98]"
            aria-label="Next jobs"
          >
            <ChevronRight size={28} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeaturedJobsCarousel;
